import { type ColumnDef } from "@tanstack/react-table";
import React, { type FC } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  Actions,
  DateTableCell,
  ReactTableComponent,
} from "../../../../components/ui-kit";
import { AppRoutes } from "../../../../constants/routes";
import { PreRegistrationApiType } from "../../../../util/types";

const Cell = styled.div`
  padding: 15px 10px;
`;

const statusColors: Record<
  string,
  { bg: string; color: string; border: string }
> = {
  pending: { bg: "#fff8ec", color: "#f5a623", border: "#f5c878" },
  registered: { bg: "#f0f7ff", color: "#027aff", border: "#9bc8ff" },
  claimed: { bg: "#e8f8f2", color: "#2cd19e", border: "#2cd19e" },
};

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) => statusColors[$status]?.bg ?? "#f5f5f5"};
  color: ${({ $status }) => statusColors[$status]?.color ?? "#666"};
  border: 1px solid ${({ $status }) => statusColors[$status]?.border ?? "#ddd"};
`;

const ClaimedLink = styled.span`
  color: #027aff;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: #0056cc;
  }
`;

function pricingSummary(pr: PreRegistrationApiType): string {
  if (pr.hasBespokePricing) return "Bespoke";
  if (pr.hasHighRiskSubscriptionPricing) return "High Risk";
  if (pr.customPercentageFee != null) {
    const flat =
      pr.customFlatFeePerTransaction != null &&
      pr.customFlatFeePerTransaction > 0
        ? `£${(pr.customFlatFeePerTransaction / 100).toFixed(2)} + `
        : "";
    const min =
      pr.customPercentageFeeMinimum != null
        ? ` (min £${(pr.customPercentageFeeMinimum / 100).toFixed(2)})`
        : "";
    return `${flat}${pr.customPercentageFee}%${min}`;
  }
  if (pr.customFlatFeePerTransaction != null) {
    return `Custom flat fee £${(pr.customFlatFeePerTransaction / 100).toFixed(
      2
    )}/tx`;
  }
  return "Standard";
}

interface PreRegistrationsListProps {
  data: PreRegistrationApiType[];
  onEdit: (preReg: PreRegistrationApiType) => void;
  onDelete: (id: string) => void;
}

const PreRegistrationsList: FC<PreRegistrationsListProps> = ({
  data,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();

  const columns = React.useMemo<Array<ColumnDef<PreRegistrationApiType, any>>>(
    () => [
      {
        header: "Email",
        meta: { mobileTitle: true },
        accessorKey: "email",
        enableSorting: false,
        cell: ({ getValue }) => <Cell>{getValue()}</Cell>,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableSorting: false,
        cell: ({ getValue }) => (
          <Cell>
            <StatusBadge $status={getValue()}>{getValue()}</StatusBadge>
          </Cell>
        ),
      },
      {
        header: "Pricing",
        accessorKey: "hasBespokePricing",
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <Cell>{pricingSummary(original)}</Cell>
        ),
      },
      {
        header: "Interim billing",
        accessorKey: "paymentsInterimBillingInterval",
        enableSorting: false,
        cell: ({ getValue }) => <Cell>{getValue() ?? "Monthly only"}</Cell>,
      },
      {
        header: "Claimed company",
        accessorKey: "claimedCompanyId",
        enableSorting: false,
        cell: ({ getValue }) => {
          const claimed = getValue() as
            | { _id: string; name?: string; identifier?: string }
            | undefined;
          return (
            <Cell>
              {claimed ? (
                <ClaimedLink
                  onClick={() =>
                    navigate(
                      `${AppRoutes.Private.Companies.DETAILS}${claimed._id}`
                    )
                  }
                >
                  {claimed.name || claimed.identifier || claimed._id}
                </ClaimedLink>
              ) : (
                <span style={{ color: "#aaa" }}>—</span>
              )}
            </Cell>
          );
        },
      },
      {
        header: "Created",
        accessorKey: "createdAt",
        enableSorting: false,
        cell: (info) => (
          <Cell>
            <DateTableCell date={info.getValue()} />
          </Cell>
        ),
      },
      {
        header: "",
        accessorKey: "actions",
        id: "actions",
        cell: ({ row: { original } }) => {
          const isClaimed = original.status === "claimed";
          if (isClaimed) return <Cell />;
          return (
            <Cell>
              <Actions
                actions={[
                  {
                    label: "Edit",
                    onClick: () => onEdit(original),
                  },
                  {
                    label: "Delete",
                    color: "#e53e3e",
                    onClick: () => onDelete(original._id),
                  },
                ]}
              />
            </Cell>
          );
        },
      },
    ],
    [onEdit, onDelete, navigate]
  );

  return <ReactTableComponent columns={columns} data={data} padding="0" />;
};

export default PreRegistrationsList;
